import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { Box, ButtonBase, Typography, Link, Button } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import NewspaperOutlinedIcon from '@mui/icons-material/NewspaperOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { useAccountAuth } from '../hooks/useAccountAuth';
import { useEntitlement } from '../hooks/useEntitlement';
import { openPrivacyPolicy } from '../lib/privacyPolicy';
import { usePosts, markPostsSeen } from '../data/posts';
import { getFullAppPurchase } from '../data/entitlements';
import { isUnread } from '../lib/postDates';
import { track } from '../utils/analytics';
import { YouCard, YouRow, YouRowGroup, PrimaryButton, softButtonSx } from './YouPrimitives';
import PageShell, { PageTitle } from './PageShell';
import useSplitLayout from '../hooks/useSplitLayout';
import { PAGE_GUTTER, PAGE_TOP } from '../config/layout';
import PurchaseCard from './PurchaseCard';
import SignUpSheet from './SignUpSheet';
import WhatsNewSection from './WhatsNewSection';
import NewsList from './NewsList';
import HelpSection from './HelpSection';
import FaqSection from './FaqSection';
import KnownIssuesSection from './KnownIssuesSection';
import Settings, { appVersion } from './Settings';
import RateRow from './RateRow';
import AccountSection from './AccountSection';
import NewsletterSection from './NewsletterSection';

const parentOf = (section) => (section === 'faq' || section === 'issues' ? 'help' : null);

// Le seuil de bascule et la gouttiere vivent dans config/layout : Jam decide
// sur les memes valeurs.

const frameSx = { display: 'flex', flexDirection: 'column', gap: '16px' };
const Frame = ({ children }) => <Box sx={frameSx}>{children}</Box>;

// Rappel de la proposition de compte après un achat. La feuille d'inscription
// ne passe qu'une fois, une seconde après l'achat (UnlockSheet), et une
// fermeture la fait disparaître pour de bon : les acheteurs sans compte sont
// tous passés par là. Ce rappel-ci ne s'impose pas et ne se ferme pas non plus
// définitivement, il revient tant que l'achat n'est pas rattaché à un compte.
//
// compact : colonne étroite du maître-détail, même règle que PurchaseCard.
const SecurePurchaseBanner = ({ onCreateAccount, onDismiss, compact = false }) => (
  <YouCard
    sx={{
      background:
        'linear-gradient(135deg, rgba(var(--accent-rgb), 0.16), rgba(var(--accent-rgb), 0.04)), #141416',
    }}
  >
    <Typography sx={{ fontSize: 17, fontWeight: 600, color: '#fff' }}>
      Your purchase lives on this device
    </Typography>
    <Typography
      sx={{ fontSize: 13, fontWeight: 300, color: 'rgba(255,255,255,0.7)', mt: '6px', mb: '14px' }}
    >
      Create an account and it follows you to your phone, your tablet, and any browser you sign in
      from.
    </Typography>
    <PrimaryButton
      onClick={onCreateAccount}
      sx={compact ? { height: 40, fontSize: 13 } : undefined}
    >
      Secure my purchase
    </PrimaryButton>
    <Button
      fullWidth
      variant="contained"
      disableElevation
      onClick={onDismiss}
      sx={{ ...softButtonSx, mt: '8px', height: compact ? 36 : 40 }}
    >
      Later
    </Button>
  </YouCard>
);

const YouPage = forwardRef(function YouPage(
  {
    active = true,
    bassMode,
    onBassModeChange,
    onUnlock,
    screen = 'you',
    isDesktop = false,
    desktopBrowser = false,
  },
  ref
) {
  const [section, setSection] = useState(null);
  // News a amener sous les yeux quand la liste s'ouvre depuis une carte du
  // carrousel. Null des qu'on ouvre la liste par "All news" ou par la rangee.
  const [focusPostId, setFocusPostId] = useState(null);
  const rootRef = useRef(null);
  // Une seule feuille de compte pour la page, ouverte selon l'entrée : le
  // rappel d'achat ('purchase'), la ligne d'identité ('default') ou le lien de
  // la carte d'achat ('restore', directement en connexion).
  const [signUpVariant, setSignUpVariant] = useState('default');
  const [signUpOpen, setSignUpOpen] = useState(false);
  const openSignUp = (variant) => {
    setSignUpVariant(variant);
    setSignUpOpen(true);
  };
  // "Later" ne vit qu'en mémoire : rien dans le stockage, donc le rappel
  // revient au lancement suivant tant que l'achat n'a pas de compte.
  const [securePromptDismissed, setSecurePromptDismissed] = useState(false);
  const { user, signedIn } = useAccountAuth();
  const { fullApp } = useEntitlement();
  const { feed, byType, lastSeenAt, loaded } = usePosts();

  // Visiter la page marque tout comme lu, mais les pastilles doivent rester
  // visibles pendant la visite : on fige la valeur d'avant le marquage.
  const lastSeenRef = useRef(lastSeenAt);
  const feedRef = useRef(feed);
  const [seenBefore, setSeenBefore] = useState(lastSeenAt);
  const marked = useRef(false);

  useEffect(() => {
    lastSeenRef.current = lastSeenAt;
    feedRef.current = feed;
  }, [lastSeenAt, feed]);

  useEffect(() => {
    if (!active || loaded === false) {
      if (!active) marked.current = false;
      return;
    }
    if (marked.current) return;
    marked.current = true;
    const unreadBefore = feedRef.current.filter((post) => isUnread(post, lastSeenRef.current)).length;
    setSeenBefore(lastSeenRef.current);
    markPostsSeen();
    track('posts_seen', { unread_before: unreadBefore });
  }, [active, loaded]);

  // La coquille mesure sa propre largeur, deja plafonnee : l'effet passif de
  // you_opened lit splitRef, ecrit en phase de layout.
  const { width, split, splitRef } = useSplitLayout(rootRef, active);

  const leftWidth = isDesktop ? 280 : width >= 900 ? 340 : 300;

  // Un tap explicite sur une rangee ou un lien. Les retours passent par
  // setSection directement : on ne mesure que les ouvertures.
  const openSection = useCallback((next, postId = null) => {
    track('you_section_opened', { section: next });
    setFocusPostId(postId);
    setSection(next);
  }, []);

  const backToList = useCallback(() => setSection(null), []);
  const backToHelp = useCallback(() => setSection('help'), []);

  // Retour Android : meme contrat que LessonPlayer.handleBack. On ferme le
  // sous-ecran ouvert et on rend true, sinon false et App sort de la page.
  // En maitre-detail la colonne de gauche ne disparait jamais : seuls la FAQ et
  // les bugs connus ont un retour, ailleurs le geste doit sortir de la page.
  const handleBack = useCallback(() => {
    if (split) {
      if (section === 'faq' || section === 'issues') {
        setSection('help');
        return true;
      }
      return false;
    }
    if (section) {
      setSection(parentOf(section));
      return true;
    }
    return false;
  }, [section, split]);

  useImperativeHandle(ref, () => ({ handleBack }), [handleBack]);

  useEffect(() => {
    if (!active) {
      setSection(null);
      setFocusPostId(null);
    }
  }, [active]);

  // Une ouverture, pas un rendu : le drapeau se rearme quand la page se ferme,
  // et la largeur n'est pas une dependance (changer de taille n'est pas rouvrir).
  const openTracked = useRef(false);
  useEffect(() => {
    if (!active) {
      openTracked.current = false;
      return;
    }
    if (openTracked.current) return;
    openTracked.current = true;
    track('you_opened', { layout: splitRef.current ? 'split' : 'list' });
  }, [active, splitRef]);

  const email = user?.email || '';
  const initial = email ? email.charAt(0).toUpperCase() : '';
  const unreadCount = feed.filter((post) => isUnread(post, seenBefore)).length;

  // La section par defaut du maitre-detail est derivee, jamais rangee dans
  // l'etat : ni flash au premier rendu, ni effet de remise a zero.
  const effectiveSection = split ? section ?? 'news' : section;

  // Un achat de store (ou de web billing, même source) sans compte pour le
  // porter : il ne survit qu'au Restore de la plateforme où il a été fait. Un
  // accès 'mock', 'admin', Patreon ou beta n'a pas ce problème, il ne tient pas
  // à un compte de store. La lecture se fait au rendu : useEntitlement abonne
  // déjà la page au miroir user, donc la bannière suit l'achat qui arrive.
  const showSecurePrompt =
    !signedIn && !securePromptDismissed && getFullAppPurchase()?.source === 'revenuecat';

  const secureBanner = (compact) =>
    showSecurePrompt ? (
      <SecurePurchaseBanner
        compact={compact}
        onCreateAccount={() => openSignUp('purchase')}
        onDismiss={() => setSecurePromptDismissed(true)}
      />
    ) : null;

  // La feuille se ferme d'elle-même dès que la session est identifiée
  // (SignUpSheet), et la bannière disparaît par la même condition.
  const secureSheet = (
    <SignUpSheet
      open={signUpOpen}
      onClose={() => setSignUpOpen(false)}
      variant={signUpVariant}
    />
  );

  // La ligne d'identité en tête de page : un invité y trouve la connexion,
  // un compte y retrouve sa section Account.
  const openIdentity = () => {
    if (signedIn) openSection('account');
    else {
      track('you_identity_signin');
      openSignUp('default');
    }
  };
  const onSignIn = signedIn
    ? undefined
    : () => {
        track('purchase_card_signin');
        openSignUp('restore');
      };

  if (split) {
    const selectedRow = parentOf(effectiveSection) || effectiveSection;

    let detail = null;
    if (effectiveSection === 'help') {
      detail = (
        <HelpSection
          screen={screen}
          faqCount={byType.faq.length}
          knownIssues={byType.known_issue}
          onOpenFaq={() => openSection('faq')}
          onOpenKnownIssues={() => openSection('issues')}
        />
      );
    } else if (effectiveSection === 'faq') {
      detail = <FaqSection items={byType.faq} onBack={backToHelp} />;
    } else if (effectiveSection === 'issues') {
      detail = <KnownIssuesSection items={byType.known_issue} onBack={backToHelp} />;
    } else if (effectiveSection === 'settings') {
      detail = <Settings bassMode={bassMode} onBassModeChange={onBassModeChange} wide />;
    } else if (effectiveSection === 'newsletter') {
      detail = <NewsletterSection signedIn={signedIn} userId={user?.id} />;
    } else if (effectiveSection === 'account') {
      detail = <AccountSection />;
    } else {
      detail = <NewsList posts={feed} seenBefore={seenBefore} focusId={focusPostId} />;
    }

    return (
      <PageShell
        ref={rootRef}
        label="You"
        desktopBrowser={desktopBrowser}
        className="you-scroll"
        sx={{ display: 'flex', alignItems: 'flex-start' }}
      >
        <Box
          sx={{
            flex: `0 0 ${leftWidth}px`,
            position: 'sticky',
            // Collee au meme creux que celui ou elle demarre : avec top 0 elle
            // montait des 16 px du recul de la coquille au premier cran de
            // molette, puis se figeait. Un saut pour rien.
            top: `${PAGE_TOP}px`,
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            paddingRight: `${PAGE_GUTTER + 8}px`,
            paddingBottom: '24px',
          }}
        >
          <PageTitle>You</PageTitle>

          <ButtonBase
            onClick={openIdentity}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: '10px',
              minWidth: 0,
              textAlign: 'left',
              borderRadius: '12px',
              margin: '-6px',
              padding: '6px',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.04)' },
            }}
          >
            {signedIn && initial ? (
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  backgroundColor: 'var(--accent)',
                  color: '#0a0a0c',
                  fontSize: 15,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {initial}
              </Box>
            ) : (
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  backgroundColor: '#1c1c1f',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <PersonOutlineOutlinedIcon sx={{ fontSize: 20, color: 'rgba(255,255,255,0.7)' }} />
              </Box>
            )}

            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 14,
                  color: 'rgba(255,255,255,0.7)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {signedIn && email ? email : 'Guest'}
              </Typography>

              {!signedIn && (
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--accent)', mt: '2px' }}>
                  Sign in or create an account
                </Typography>
              )}

              {fullApp && (
                <Typography
                  sx={{
                    display: 'inline-block',
                    fontSize: 11,
                    fontWeight: 600,
                    color: 'var(--accent)',
                    backgroundColor: 'rgba(var(--accent-rgb), 0.14)',
                    borderRadius: '20px',
                    padding: '3px 8px',
                    mt: '4px',
                  }}
                >
                  Full access
                </Typography>
              )}
            </Box>

            <ChevronRightIcon
              sx={{ fontSize: 20, color: 'rgba(255,255,255,0.4)', ml: 'auto', flexShrink: 0 }}
            />
          </ButtonBase>

          {/* Dans la colonne de gauche, jamais dans le détail : elle reste
              visible quelle que soit la section ouverte. */}
          {secureBanner(true)}

          {!fullApp && <PurchaseCard onUnlock={onUnlock} onSignIn={onSignIn} compact />}

          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <YouRow
              dense
              icon={<NewspaperOutlinedIcon />}
              title="What's new"
              selected={selectedRow === 'news'}
              onClick={() => openSection('news')}
              trailing={
                unreadCount > 0 ? (
                  <Box
                    sx={{
                      minWidth: 20,
                      height: 20,
                      borderRadius: '10px',
                      padding: '0 6px',
                      backgroundColor: '#e5484d',
                      color: '#fff',
                      fontSize: 12,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {unreadCount}
                  </Box>
                ) : null
              }
            />
            <YouRow
              dense
              accent
              icon={<HelpOutlineIcon />}
              title="Help & Support"
              selected={selectedRow === 'help'}
              onClick={() => openSection('help')}
            />
            <YouRow
              dense
              icon={<EmailOutlinedIcon />}
              title="Newsletter"
              selected={selectedRow === 'newsletter'}
              onClick={() => openSection('newsletter')}
            />
            <RateRow dense />
            <YouRow
              dense
              icon={<SettingsOutlinedIcon />}
              title="Settings"
              selected={selectedRow === 'settings'}
              onClick={() => openSection('settings')}
            />
            <YouRow
              dense
              icon={<PersonOutlineOutlinedIcon />}
              title="Account"
              selected={selectedRow === 'account'}
              onClick={() => openSection('account')}
            />
          </Box>

          <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>
            {`upScale ${appVersion} · `}
            <Link
              component="button"
              underline="hover"
              onClick={openPrivacyPolicy}
              sx={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', verticalAlign: 'baseline' }}
            >
              Privacy policy
            </Link>
          </Typography>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ maxWidth: 1040, paddingBottom: '40px' }}>{detail}</Box>
        </Box>

        {secureSheet}
      </PageShell>
    );
  }

  // La coquille donne la gouttiere et le plafond de la page ; la liste garde
  // le sien, plus etroit, pour rester identique au pixel pres sur telephone.
  const inList = (children) => (
    <PageShell ref={rootRef} label="You" desktopBrowser={desktopBrowser} className="you-scroll">
      <Box sx={{ maxWidth: 500, mx: 'auto' }}>
        <Frame>{children}</Frame>
      </Box>
    </PageShell>
  );

  if (section === 'news') {
    return inList(
      <NewsList posts={feed} seenBefore={seenBefore} onBack={backToList} focusId={focusPostId} />
    );
  }
  if (section === 'help') {
    return inList(
      <HelpSection
        onBack={backToList}
        screen={screen}
        faqCount={byType.faq.length}
        knownIssues={byType.known_issue}
        onOpenFaq={() => openSection('faq')}
        onOpenKnownIssues={() => openSection('issues')}
      />
    );
  }
  if (section === 'faq') {
    return inList(<FaqSection items={byType.faq} onBack={backToHelp} />);
  }
  if (section === 'issues') {
    return inList(<KnownIssuesSection items={byType.known_issue} onBack={backToHelp} />);
  }
  if (section === 'settings') {
    return inList(
      <Settings onBack={backToList} bassMode={bassMode} onBassModeChange={onBassModeChange} />
    );
  }
  if (section === 'newsletter') {
    return inList(<NewsletterSection signedIn={signedIn} userId={user?.id} onBack={backToList} />);
  }
  if (section === 'account') return inList(<AccountSection onBack={backToList} />);

  return inList(
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
        <PageTitle>You</PageTitle>

        <ButtonBase
          onClick={openIdentity}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            backgroundColor: '#141416',
            borderRadius: '20px',
            padding: '6px 12px 6px 6px',
            minWidth: 0,
          }}
        >
          {signedIn && initial ? (
            <Box
              sx={{
                width: 26,
                height: 26,
                borderRadius: '50%',
                backgroundColor: 'var(--accent)',
                color: '#0a0a0c',
                fontSize: 13,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {initial}
            </Box>
          ) : (
            <Box
              sx={{
                width: 26,
                height: 26,
                borderRadius: '50%',
                backgroundColor: '#1c1c1f',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <PersonOutlineOutlinedIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.7)' }} />
            </Box>
          )}

          <Typography
            sx={{
              fontSize: 12,
              color: 'rgba(255,255,255,0.7)',
              maxWidth: 170,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {signedIn && email ? email : 'Guest'}
          </Typography>

          {fullApp && (
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--accent)',
                backgroundColor: 'rgba(var(--accent-rgb), 0.14)',
                borderRadius: '20px',
                padding: '3px 8px',
                flexShrink: 0,
              }}
            >
              Full access
            </Typography>
          )}

          {!signedIn && (
            <Typography
              sx={{ fontSize: 12, fontWeight: 600, color: 'var(--accent)', flexShrink: 0 }}
            >
              Sign in
            </Typography>
          )}
        </ButtonBase>
      </Box>

      {secureBanner(false)}

      {!fullApp && <PurchaseCard onUnlock={onUnlock} onSignIn={onSignIn} />}

      <WhatsNewSection
        feed={feed.slice(0, 3)}
        unreadCount={unreadCount}
        seenBefore={seenBefore}
        onAllNews={(postId) => openSection('news', postId)}
      />

      <YouRowGroup>
        <YouRow
          icon={<HelpOutlineIcon />}
          accent
          title="Help & Support"
          subtitle="Report a bug, FAQ, known issues, community"
          onClick={() => openSection('help')}
        />
        <YouRow
          icon={<EmailOutlinedIcon />}
          title="Newsletter"
          subtitle="Guitar tips, new features and news"
          onClick={() => openSection('newsletter')}
        />
        <RateRow />
        <YouRow
          icon={<SettingsOutlinedIcon />}
          title="Settings"
          subtitle="Audio, tuning"
          onClick={() => openSection('settings')}
        />
        <YouRow
          icon={<PersonOutlineOutlinedIcon />}
          title="Account"
          subtitle="Access, purchases, privacy, delete"
          onClick={() => openSection('account')}
        />
      </YouRowGroup>

      <Typography
        sx={{
          textAlign: 'center',
          fontSize: 12,
          color: 'rgba(255,255,255,0.4)',
        }}
      >
        {`upScale ${appVersion} · `}
        <Link
          component="button"
          underline="hover"
          onClick={openPrivacyPolicy}
          sx={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', verticalAlign: 'baseline' }}
        >
          Privacy policy
        </Link>
      </Typography>

      {secureSheet}
    </>
  );
});

export default YouPage;
